import { ApplicationRef, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, SimpleChanges, ViewChild, ElementRef, Renderer2, OnDestroy, HostListener } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import Swal from 'sweetalert2';
import { ToastrService } from 'ngx-toastr';
import { SuperAdminService } from "src/app/super-admin/super-admin.service";
import { LanguageService } from "../report-v2/services/language.service";
import { TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { InstitutionSettings } from 'src/app/models/institution-settings.interface';
import { MatChipInputEvent } from "@angular/material/chips";
import { MatSelectChange } from "@angular/material/select";
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Handle, SliderRange } from '../../shared/components/custom-slider/custom-slider.component';

@Component({
  selector: 'app-admin-settings',
  templateUrl: './admin-settings.component.html',
  styleUrls: ['./admin-settings.component.scss']
})

export class AdminSettingsComponent implements OnInit, OnDestroy {
  initialFormValues: any;
  institutionSettings: InstitutionSettings | null = null;
  institutionsAccess: any = {};

  // Content Access UI properties
  contentAccessEnabled = false;
  institutionsList: any[] = [];
  selectedInstitutions: Set<string> = new Set();
  expandedInstitutions: Set<string> = new Set();
  bulkAccessLevel = 'closed';
  pendingAccessChanges: { [institutionId: string]: string } = {}; // Track pending access changes

  // Pagination properties
  page = 1;
  pageSize: string | number = "5";  // Start as string for ngModel compatibility
  totalInstitutions = 0;

  // Dropdown properties
  dropdownOpen = false;
  selectedOption = 'percentage';
  UIDropdown = false;
  uiSelectedOption = 'percentage';

  languages = [
    {
      text: 'English',
      value: 'en',
    },
    {
      text: 'Albanian',
      value: 'sq',
    },
    {
      text: 'German',
      value: 'de',
    },
    {
      text: 'Italian',
      value: 'it',
    },
    {
      text: 'French',
      value: 'fr',
    },
    {
      text: 'Spanish',
      value: 'es',
    },
    {
      text: 'Greek',
      value: 'el',
    },
    {
      text: 'Czech',
      value: 'cs',
    },
    {
      text: 'Turkish',
      value: 'tr',
    },
    {
      text: 'Slovak',
      value: 'sk',
    },
    {
      text: 'Lithuanian',
      value: 'lt',
    },
    {
      text: 'Latvian',
      value: 'lv',
    },
    {
      text: 'Polish',
      value: 'pl',
    },
    {
      text: 'Serbian',
      value: 'sr',
    },
    {
      text: 'Macedonian',
      value: 'mk',
    },
    {
      text: 'Portuguese',
      value: 'pt',
    },
    {
      text: 'Dutch',
      value: 'nl',
    },
    {
      text: 'Russian',
      value: 'ru',
    },
    {
      text: 'Bulgarian',
      value: 'bg',
    },
    {
      text: 'Hungarian',
      value: 'hu',
    },
    {
      text: 'Romanian',
      value: 'ro',
    },
    {
      text: 'Slovenian',
      value: 'sl',
    },
    {
      text: 'Swedish',
      value: 'sv',
    },
    {
      text: 'Finnish',
      value: 'fi',
    },
    {
      text: 'Croatian',
      value: 'hr',
    },
    {
      text: 'Bosnian',
      value: 'bs',
    },
    {
      text: 'Norwegian',
      value: 'no',
    },
    {
      text: 'Danish',
      value: 'da'
    },
    {
      text: 'Estonian',
      value: 'et'
    }
  ];

  translatedLanguageEnabled = new FormControl(false); // Using FormControl for the toggle
  selectedTranslatedLanguages = new FormControl([], [Validators.maxLength(3)]);

  allChipOptions: { value: string, title: string }[] = [
    { value: "PERSON", title: "Person Name" },
    { value: "PHONE_NUMBER", title: "Phone Number" },
    { value: "IP_ADDRESS", title: "IP Address" },
    { value: "LOCATION", title: "Location" },
    { value: "IBAN_CODE", title: "IBAN Code" },
    { value: "NRP", title: "nrp" },
    { value: "DATE_TIME", title: "Date and Time" },
    { value: "URL", title: "URL" },
    // { value: "MEDICAL_LICENSE", title: "Medical License" },
    // { value: "BTC_ADDRESS", title: "Bitcoin Address" },
    // { value: "UUID", title: "UUID" },
    { value: "EMAIL_ADDRESS", title: "Email Address" },
    { value: "CREDIT_CARD", title: "Credit Card Number" }
  ];

  defaultChipOptions = [
    { value: "EMAIL_ADDRESS", title: "Email Address" },
    { value: "CREDIT_CARD", title: "Credit Card Number" },
    { value: "NRP", title: "nrp" }
  ];

  archive: boolean = false;
  isFormChanged = false;
  selectedContentInfo: { text: string } | null = null;
  selectedContentValue: string = '';

  defaultRiskLevels = [
    { from: 80, to: 100, label: 'High risk', class: 'high' },
    { from: 20, to: 80, label: 'Medium risk', class: 'medium' },
    { from: 0, to: 20, label: 'Low risk', class: 'low' }
  ];

  admin_settings = this.fb.group({
    documentTranslatedLanguage: this.selectedTranslatedLanguages,
    translatedLanguageEnabled: this.translatedLanguageEnabled,
    aiDetection: [false],
    metadataAnalysis: [false],
    stylometricsAnalysis: [false],
    submissionPreCheck: [false],
    contextualSimilarity: [false],
    enableThresholds: [false],
    riskLevels: [this.defaultRiskLevels],
    originalityReportScoreDisplay: ["percentage"],
    userInterfaceScoreDisplay: ["percentage"],
    enableRiskClassification: [true],
    exactMatchThreshold: [80, [Validators.max(100)]],
    alteredTextThreshold: [40, [Validators.max(100)]],
    contextualSimilaritiesThreshold: [80, [Validators.max(100), Validators.min(50)]],
    archive: [false],
    contentAccess: ["closed"],
    institutionsContentAccess: [],
    personalIdentifiableInformation: [false],
    canRegisterStudents: [false],
    personalIdentifiableInformationInfo: this.defaultChipOptions || []
  });

  chipInputControl = new FormControl();
  chips: string[] = [];
  filteredOptions: Observable<string[]>;
  showChipInput = false;
  filteredChipOptions: Observable<{ value: string, title: string }[]>;

  @ViewChild('chipInput') chipInput!: ElementRef;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChild('addMoreButton') addMoreButton!: ElementRef;

  private clickListener: (() => void) | null = null;

  handles = [
    { value: 60, min: 40, max: 90 },  // Altered Text
    { value: 80, min: 60, max: 90 } // Exact Match
  ];

  // First slider configuration
  similarityHandles: Handle[] = [
    { value: 60, min: 40, max: 90 },
    { value: 80, min: 60, max: 90 }
  ];
  similarityDefaultValues: number[] = [];

  // Second slider configuration for contextual similarity
  translationHandles: Handle[] = [
    { value: 50, min: 0, max: 50, hardMinimum: 50 },
    { value: 80, min: 50, max: 95 }
  ];
  translationDefaultValues: number[] = [];

  similarityRangeColors: SliderRange[] = [
    { color: '#BCBCBC', label: 'Below Threshold' },
    { color: '#7ABDFF', label: 'Altered Text' },
    { color: '#F386AB', label: 'Exact Match' }
  ];

  translationRangeColors: SliderRange[] = [
    { color: '#D9D9D9', label: '' },
    { color: '#BCBCBC', label: '' },
    { color: '#FED27C', label: 'Contextual Similarity' }
  ];

  isDragging = false;
  activeHandle: number | null = null;

  options = [
    { value: 'riskFactor', label: '' },
    { value: 'percentage', label: '' },
    { value: 'none', label: '' }
  ];

  UIoptions = [
    { value: 'riskFactor', label: '' },
    { value: 'percentage', label: '' },
  ];

  constructor(
    private fb: FormBuilder,
    private toastrService: ToastrService,
    private superAdminService: SuperAdminService,
    private languageService: LanguageService,
    private translate: TranslateService,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2
  ) { }

  // Make Math available in template
  Math = Math;

  ngOnInit(): void {
    const storedLanguage = localStorage.getItem('websiteLanguage');
    const language = storedLanguage ? storedLanguage : 'en';

    this.translate.use(language);
    this.languageService.selectedLanguage$.subscribe((language: string) => {
      this.translate.use(language);
    });
    this.languages.sort((a, b) => a.text.localeCompare(b.text));

    this.updateOptionLabels();

    // Subscribe to language changes
    this.translate.onLangChange.subscribe(() => {
      this.updateOptionLabels();
    });

    this.initializeForm();
    this.loadInstitutionSettings();


    this.setupFormSubscriptions();
  }

  /**
   * Initialize the reactive form with default values
   */
  private initializeForm(): void {
    this.admin_settings = this.fb.group({
      documentTranslatedLanguage: this.selectedTranslatedLanguages,
      translatedLanguageEnabled: this.translatedLanguageEnabled,
      aiDetection: [false],
      metadataAnalysis: [false],
      stylometricsAnalysis: [false],
      submissionPreCheck: [false],
      contextualSimilarity: [false],
      enableThresholds: [false],
      originalityReportScoreDisplay: ["percentage"],
      userInterfaceScoreDisplay: ["percentage"],
      riskLevels: [this.defaultRiskLevels],
      enableRiskClassification: [true], // Always enabled since toggle is disabled
      exactMatchThreshold: [80, [Validators.max(100)]],
      alteredTextThreshold: [40, [Validators.max(100)]],
      contextualSimilaritiesThreshold: [80, [Validators.max(100), Validators.min(50)]],
      archive: [false],
      contentAccess: ["closed"],
      institutionsContentAccess: [],
      personalIdentifiableInformation: [false],
      canRegisterStudents: [false],
      personalIdentifiableInformationInfo: this.defaultChipOptions || [],
    });
  }

  /**
   * Load institution settings from the API
   */
  private loadInstitutionSettings(): void {
    this.spinner.show();

    this.superAdminService.getInstitutionSettings(0, 'true').subscribe({
      next: (response) => {
        this.institutionSettings = response.institutionSettings;
        this.institutionsAccess = response.institutionsAccess;

        console.log('institutionSettings', this.institutionSettings);


        // Process institutions data for content access UI
        this.processInstitutionsData();

        // If institutionSettings is null, try localStorage fallback or use defaults
        if (!this.institutionSettings) {
          console.log('Institution settings is null, trying localStorage fallback');
          this.loadSettingsFromLocalStorage();
        } else {
          this.populateFormWithSettings();
        }

        this.spinner.hide();
      },
      error: (error) => {
        console.error('Error loading institution settings:', error);
        this.toastrService.error(this.translate.instant('general.error_occurred'));
        this.spinner.hide();
        // Fallback to localStorage if API fails
        this.loadSettingsFromLocalStorage();
      }
    });
  }

  /**
   * Populate the form with settings from API response
   */
  private populateFormWithSettings(): void {
    if (!this.institutionSettings) return;

    const settings = this.institutionSettings;

    // Map API response to form structure
    this.admin_settings.patchValue({
      documentTranslatedLanguage: settings.translatedLanguages || [],
      translatedLanguageEnabled: !!settings.enableTranslatedLanguages, // Add this line
      aiDetection: !!settings.enableAiDetection,
      metadataAnalysis: !!settings.enableMetadataAnalysis,
      stylometricsAnalysis: false, // Not in new schema
      submissionPreCheck: !!settings.enableStudentSubmissionPrecheck,
      contextualSimilarity: !!settings.enableContextualSimilarity,
      enableThresholds: !!settings.enableSimilarityThresholdsEdit,
      exactMatchThreshold: settings.exactMatchSimilarityThreshold || 80,
      alteredTextThreshold: settings.alteredTextSimilarityThreshold || 40,
      contextualSimilaritiesThreshold: settings.contextualSimilarityThreshold || 80,
      archive: !!settings.enableArchiveDocuments,
      contentAccess: "closed", // Default for now, will be enhanced later
      institutionsContentAccess: [],
      personalIdentifiableInformation: !!settings.enablePersonalIdentifiableInformation,
      canRegisterStudents: !!settings.enableStudentRegistration,
      personalIdentifiableInformationInfo: this.mapPersonalInfoChips(settings.personalIdentifiableInformationList),
      originalityReportScoreDisplay: this.mapBackendToFrontend(settings.originalityDisplay) || "percentage",
      userInterfaceScoreDisplay: this.mapBackendToFrontend(settings.userInterfaceDisplay) || "percentage",
      enableRiskClassification: true, // Always enable since we have risk levels
      riskLevels: []
    });

    // Update risk levels with values from the backend
    this.defaultRiskLevels = [
      { from: settings.originalityRiskFactorHighRisk || 80, to: 100, label: 'High risk', class: 'high' },
      { from: settings.originalityRiskFactorMediumRisk || 20, to: settings.originalityRiskFactorHighRisk || 80, label: 'Medium risk', class: 'medium' },
      { from: 0, to: settings.originalityRiskFactorMediumRisk || 20, label: 'Low risk', class: 'low' }
    ];

    // Update dropdown selections
    this.selectedOption = this.mapBackendToFrontend(settings.originalityDisplay) || "percentage";
    this.uiSelectedOption = this.mapBackendToFrontend(settings.userInterfaceDisplay) || "percentage";

    // Update translated language toggle
    this.translatedLanguageEnabled.setValue(settings.enableTranslatedLanguages);
    this.selectedTranslatedLanguages.setValue(settings.translatedLanguages || []);

    // Update sliders
    this.updateSliderValues();

    setTimeout(() => {
      this.initialFormValues = this.admin_settings.getRawValue();
    }, 0);
  }

  /**
   * Map personal identifiable information list to chip format
   */
  private mapPersonalInfoChips(infoList: string[] | null): { value: string, title: string }[] {
    if (!infoList || !Array.isArray(infoList)) return this.defaultChipOptions;

    return infoList.map(value => {
      const matchingOption = this.allChipOptions.find(option => option.value === value);
      return matchingOption || { value, title: value };
    });
  }

  /**
   * Update slider values based on settings
   */
  private updateSliderValues(): void {
    if (!this.institutionSettings) return;

    const settings = this.institutionSettings;

    // Update similarity slider handles
    this.similarityHandles = [
      { value: settings.alteredTextSimilarityThreshold || 60, min: 40, max: 90 },
      { value: settings.exactMatchSimilarityThreshold || 80, min: 60, max: 90 }
    ];

    this.similarityDefaultValues = [
      settings.alteredTextSimilarityThreshold || 60,
      settings.exactMatchSimilarityThreshold || 80
    ];

    // Update translation slider handles
    this.translationDefaultValues = [50, settings.contextualSimilarityThreshold || 80];

    if (settings.enableContextualSimilarity) {
      this.translationHandles = [
        { value: 50, min: 0, max: 50, hardMinimum: 50 },
        { value: settings.contextualSimilarityThreshold || 80, min: 50, max: 95 }
      ];
    }
  }

  /**
   * Fallback method to load settings from localStorage
   */
  private loadSettingsFromLocalStorage(): void {
    const storedData = localStorage.getItem('user');
    if (!storedData) {
      setTimeout(() => {
        this.initialFormValues = this.admin_settings.getRawValue();
      }, 0);
      return;
    }

    try {
      const institutionData = JSON.parse(storedData);
      if (institutionData?.Institution?.InstitutionSettings[0]) {
        // Legacy logic for localStorage - keeping as fallback
        const settings = institutionData.Institution.InstitutionSettings[0];
        this.admin_settings.patchValue({
          documentTranslatedLanguage: settings.translatedLanguage || [],
          enableTranslatedLanguages: settings.enableTranslatedLanguages,
          aiDetection: !!settings.ai,
          metadataAnalysis: !!settings.metadataAnalysis,
          stylometricsAnalysis: !!settings.stylometricsAnalysis,
          submissionPreCheck: !!settings.submission_pre_check,
          contextualSimilarity: !!settings.contextualSimilarity,
          enableThresholds: !!settings.thresholds,
          exactMatchThreshold: settings.thresholds?.exact_match || 80,
          alteredTextThreshold: settings.thresholds?.altered_text || 40,
          contextualSimilaritiesThreshold: settings.thresholds?.contextual_similarity || 80,
          archive: !!settings.archive,
          personalIdentifiableInformation: settings.personalIdentifiableInformation,
          canRegisterStudents: settings.canRegisterStudents,
          personalIdentifiableInformationInfo: settings.personalIdentifiableInformationInfo || this.defaultChipOptions
        });

        this.translatedLanguageEnabled.setValue(settings.enableTranslatedLanguages);
        this.selectedTranslatedLanguages.setValue(settings.translatedLanguage || []);
        setTimeout(() => {
          this.initialFormValues = this.admin_settings.getRawValue();
        }, 0);
      }
    } catch (error) {
      console.error('Error parsing localStorage data:', error);
      setTimeout(() => {
        this.initialFormValues = this.admin_settings.getRawValue();
      }, 0);
    }
  }

  /**
   * Setup form subscriptions for change detection and other behaviors
   */
  private setupFormSubscriptions(): void {
    this.admin_settings.valueChanges.subscribe(() => {
      this.checkIfFormChanged();
    });

    this.translatedLanguageEnabled.valueChanges.subscribe((enabled) => {
      if (!enabled) {
        this.selectedTranslatedLanguages.setValue([]);
      }
    });

    const initialContentAccess = this.admin_settings.controls['contentAccess'].value;
    this.selectedContentValue = initialContentAccess;

    this.admin_settings.controls['contentAccess'].valueChanges.subscribe((value) => {
      this.selectedContentValue = value;
    });

    this.admin_settings.get('personalIdentifiableInformation')?.valueChanges.subscribe((enabled) => {
      const currentDefaultChips = [
        { value: "EMAIL_ADDRESS", title: "Email Address" },
        { value: "CREDIT_CARD", title: "Credit Card Number" },
        { value: "NRP", title: "nrp" }
      ];

      if (enabled) {
        this.admin_settings.get('personalIdentifiableInformationInfo')?.setValue(currentDefaultChips);
      } else {
        this.admin_settings.get('personalIdentifiableInformationInfo')?.setValue([]);
      }
    });

    this.fetchChips();
    this.filterChips();
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  toggleUIDropdown() {
    this.UIDropdown = !this.UIDropdown;
  }

  selectOption(option: string) {
    this.selectedOption = option;
    this.dropdownOpen = false;
    // Emit or update form control here if needed
    this.admin_settings.get('originalityReportScoreDisplay')?.setValue(option);

  }

  selectUIOption(option: string) {
    this.uiSelectedOption = option;
    this.UIDropdown = false;
    // Emit or update form control here if needed
    this.admin_settings.get('userInterfaceScoreDisplay')?.setValue(option);

  }

  updateOptionLabels(): void {
    this.translate.get([
      'new_entries.risk_factor',
      'new_entries.percentage',
      'new_entries.none'
    ]).subscribe(translations => {
      // Update options labels
      this.options = [
        { value: 'riskFactor', label: translations['new_entries.risk_factor'] },
        { value: 'percentage', label: translations['new_entries.percentage'] },
        { value: 'none', label: translations['new_entries.none'] }
      ];

      // Update UIoptions labels
      this.UIoptions = [
        { value: 'riskFactor', label: translations['new_entries.risk_factor'] },
        { value: 'percentage', label: translations['new_entries.percentage'] }
      ];
    });
  }

  getLabel(value: string): string {
    const found = this.options.find(o => o.value === value);
    return found?.label ?? '';
  }

  getUILabel(value: string): string {
    const found = this.UIoptions.find(o => o.value === value);
    return found?.label ?? '';
  }

  getIconPath(value: string): string {
    if (!value) return 'assets/images/icons/Percentage.png'; // Default to percentage icon
    switch (value) {
      case 'riskFactor': return 'assets/images/icons/dashboard-speed.png';
      case 'percentage': return 'assets/images/icons/Percentage.png';
      case 'none': return 'assets/images/icons/x.png';
      default: return 'assets/images/icons/Percentage.png';
    }
  }

  getUIIconPath(value: string): string {
    if (!value) return 'assets/images/icons/Percentage.png'; // Default to percentage icon
    switch (value) {
      case 'riskFactor': return 'assets/images/icons/dashboard-speed.png';
      case 'percentage': return 'assets/images/icons/Percentage.png';
      default: return 'assets/images/icons/Percentage.png';
    }
  }
  ngDoCheck() {
    if (localStorage.getItem('websiteLanguage') !== this.translate.currentLang) {
      this.translate.use(localStorage.getItem('websiteLanguage'));
      this.cdr.detectChanges();
    }
  }

  checkIfFormChanged() {
    if (!this.initialFormValues) {
      this.isFormChanged = false;
      return;
    }

    const currentFormValues = this.admin_settings.getRawValue();

    // Check if form values have changed
    const formValuesChanged = JSON.stringify(currentFormValues) !== JSON.stringify(this.initialFormValues);

    // Check if there are pending access changes
    const hasPendingAccessChanges = Object.keys(this.pendingAccessChanges).length > 0;

    // Form is considered changed if either form values changed OR there are pending access changes
    this.isFormChanged = formValuesChanged || hasPendingAccessChanges;

    console.log('isFormChanged', this.isFormChanged, {
      formValuesChanged,
      hasPendingAccessChanges,
      pendingAccessChangesCount: Object.keys(this.pendingAccessChanges).length
    });
  }

  isSelected(language: string): boolean {
    return this.selectedTranslatedLanguages.value.includes(language);
  }

  get form() {
    return this.admin_settings.controls;
  }

  selectTranslatedLanguage(event: MatSelectChange) {
    this.selectedTranslatedLanguages = event.value;
    this.form.documentTranslatedLanguage.setValue(this.selectedTranslatedLanguages);
  }

  archiveDocument() {
    const currentValue = this.admin_settings.controls['archive'].value;
    this.admin_settings.controls['archive'].setValue(!currentValue);
  }

  onRiskLevelsChange(updatedLevels: any[]) {
    this.defaultRiskLevels = updatedLevels;
    this.admin_settings.patchValue({
      riskLevels: updatedLevels,
    });

    // Manually trigger form change detection since risk levels
    // changes might not be properly detected by the form valueChanges
    this.checkIfFormChanged();
  }

  /**
   * Map backend enum values to frontend dropdown values
   */
  mapBackendToFrontend(backendValue?: string): string {
    if (!backendValue) return "percentage";

    switch (backendValue) {
      case 'Percentage': return 'percentage';
      case 'riskFactor': return 'riskFactor';
      case 'None': return 'none';
      default: return 'percentage';
    }
  }

  /**
   * Map frontend dropdown values to backend enum values
   */
  mapFrontendToBackend(frontendValue?: string): string {
    if (!frontendValue) return "Percentage";

    switch (frontendValue) {
      case 'percentage': return 'Percentage';
      case 'riskFactor': return 'riskFactor';
      case 'none': return 'None';
      default: return 'Percentage';
    }
  }

  async submitSettings(skipConfirmation: boolean = false) {
    if (!skipConfirmation) {
      const result = await Swal.fire({
        title: this.translate.instant('new_entries.do_you_want_to_save_current_settings'),
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: this.translate.instant('general.yes'),
        cancelButtonText: this.translate.instant('general.no'),
        confirmButtonColor: '#4dbd74',
        cancelButtonColor: '#f86c6b',
      });

      if (!result.isConfirmed) {
        return false;
      }
    }

    if (this.admin_settings.valid) {
      const formValues = this.admin_settings.value;
      console.log(formValues.documentTranslatedLanguage, formValues?.documentTranslatedLanguage);

      // Prepare access content changes for backend
      const accessContent = this.prepareAccessContentForBackend();

      const settings = {
        ai: formValues.aiDetection ? 1 : 0,
        metadataAnalysis: formValues.metadataAnalysis,
        stylometricsAnalysis: formValues.stylometricsAnalysis,
        archive: formValues.archive,
        originalLanguage: formValues.documentTranslatedLanguage[0] || 'en',
        // translatedLanguage: formValues?.documentTranslatedLanguage.length ? formValues.documentTranslatedLanguage : [],
        submission_pre_check: formValues.submissionPreCheck ? 1 : 0,
        contextualSimilarity: formValues.contextualSimilarity ? 1 : 0,
        enableThresholds: formValues.enableThresholds,
        thresholds: {
          match: formValues.exactMatchThreshold || 0, // Backend expects "match" not "exact_match"
          altered_text: formValues.alteredTextThreshold || 0,
          contextual_similarity: formValues.contextualSimilaritiesThreshold || 0
        },
        personalIdentifiableInformation: formValues.personalIdentifiableInformation,
        canRegisterStudents: formValues.canRegisterStudents,
        personalIdentifiableInformationInfo: formValues.personalIdentifiableInformationInfo.map((item: any) => item.value),
        enableTranslatedLanguages: formValues.translatedLanguageEnabled,
        translatedLanguages: formValues.translatedLanguageEnabled && formValues?.documentTranslatedLanguage.length ? formValues.documentTranslatedLanguage : [],
        originalityReportScoreDisplay: this.mapFrontendToBackend(formValues.originalityReportScoreDisplay) || 'Percentage',
        userInterfaceScoreDisplay: this.mapFrontendToBackend(formValues.userInterfaceScoreDisplay) || 'Percentage',
        riskLevels: {
          highRisk: this.defaultRiskLevels.find(level => level.class === 'high')?.from || 80,
          mediumRisk: this.defaultRiskLevels.find(level => level.class === 'medium')?.from || 20
        },
        accessContent: accessContent // Content access changes for institutions
      };

      console.log('📤 SENDING SETTINGS TO BACKEND:', settings);
      console.log('📤 ACCESS CONTENT:', accessContent);
      console.log('📤 TRANSLATED LANGUAGES DEBUG:', {
        translatedLanguageEnabled: formValues.translatedLanguageEnabled,
        documentTranslatedLanguage: formValues.documentTranslatedLanguage,
        finalTranslatedLanguages: settings.translatedLanguages,
        enableTranslatedLanguages: settings.enableTranslatedLanguages
      });

      this.spinner.show();

      try {
        const response = await this.superAdminService.editInstitution(
          settings
        ).toPromise();

        this.toastrService.success(this.translate.instant('new_entries.institution_updated_successfully'));

        // Clear pending access changes after successful submission
        this.pendingAccessChanges = {};

        // Update local storage with the new settings
        const storedData = localStorage.getItem('user');
        if (storedData) {
          const institutionData = JSON.parse(storedData);

          // if (!institutionData.Institution.settings) {
          //   institutionData.Institution.settings = {};
          // }
          institutionData.Institution.settings = {
            ...institutionData.Institution.settings,
            ...settings,
          };

          // if (!institutionData.Institution.InstitutionSettings) {
          //   institutionData.Institution.InstitutionSettings = [{}];
          // }

          // const institutionSettings = institutionData.Institution.InstitutionSettings[0] || {};

          // institutionData.Institution.InstitutionSettings[0] = {
          //   ...institutionSettings,
          //   enableAiDetection: formValues.aiDetection,
          //   enableMetadataAnalysis: formValues.metadataAnalysis,
          //   enableStudentSubmissionPrecheck: formValues.submissionPreCheck,
          //   enableContextualSimilarity: formValues.contextualSimilarity,
          //   enableSimilarityThresholdsEdit: formValues.enableThresholds,
          //   enableArchiveDocuments: formValues.archive,
          //   enablePersonalIdentifiableInformation: formValues.personalIdentifiableInformation,
          //   enableStudentRegistration: formValues.canRegisterStudents,
          //   enableTranslatedLanguages: formValues.translatedLanguageEnabled,

          //   // Threshold values
          //   exactMatchSimilarityThreshold: formValues.exactMatchThreshold || 80,
          //   alteredTextSimilarityThreshold: formValues.alteredTextThreshold || 40,
          //   contextualSimilarityThreshold: formValues.contextualSimilaritiesThreshold || 80,

          //   // Display settings - these are crucial for the risk badge
          //   originalityDisplay: this.mapFrontendToBackend(formValues.originalityReportScoreDisplay),
          //   userInterfaceDisplay: this.mapFrontendToBackend(formValues.userInterfaceScoreDisplay),

          //   // Risk factor thresholds - these control the risk badge colors
          //   originalityRiskFactorHighRisk: this.defaultRiskLevels.find(level => level.class === 'high')?.from || 80,
          //   originalityRiskFactorMediumRisk: this.defaultRiskLevels.find(level => level.class === 'medium')?.from || 20,

          //   // Language settings
          //   translatedLanguages: formValues.translatedLanguageEnabled && formValues?.documentTranslatedLanguage.length ? formValues.documentTranslatedLanguage : [],

          //   // Personal information settings
          //   personalIdentifiableInformationList: formValues.personalIdentifiableInformationInfo.map((item: any) => item.value),
          // };


          localStorage.setItem('user', JSON.stringify(institutionData));
        }

        // Update the initial form values
        this.initialFormValues = this.admin_settings.getRawValue();
        this.isFormChanged = false;
        this.spinner.hide();
        return true;
      } catch (error) {
        this.spinner.hide();
        this.toastrService.error(this.translate.instant('new_entries.institution_update_error'));
        console.error(error);
        return false;
      }
    } else {
      console.log('Form is invalid, please correct the errors.');
      return false;
    }
  }

  cancel() {
    this.admin_settings.reset();
    this.admin_settings.setValue(this.initialFormValues);
    this.isFormChanged = false;

    // Clear pending access changes and reset institution access levels
    this.pendingAccessChanges = {};
    this.selectedInstitutions.clear();

    // Reload institution settings to reset access levels to original state
    this.processInstitutionsData();

    // Reset the translatedLanguageEnabled control (now part of the form)
    // No need to manually set it since it's part of the form group

    // Fetch and set the chips from current settings
    this.fetchChips();
    this.filterChips();
  }

  preventInput(event: KeyboardEvent) {
    event.preventDefault();
  }

  addChip(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    // Prevent manual input
    if (!this.defaultChipOptions.some(option => option.value === value.trim())) {
      return;
    }

    // Add our chip
    if ((value || '').trim()) {
      this.chips.push(value.trim());
    }

    // Reset the input value
    if (input) {
      input.value = '';
    }

    this.chipInputControl.setValue(null);

    // Hide the input after adding a chip
    this.showChipInput = false;
  }

  removeChip(chip: any): void {
    const currentChips = this.admin_settings.get('personalIdentifiableInformationInfo')?.value || [];
    const updatedChips = currentChips.filter((c: any) => c.value !== chip.value);
    this.admin_settings.get('personalIdentifiableInformationInfo')?.setValue(updatedChips);
    this.updateFilteredChipOptions();
  }

  selectChip(event: any): void {
    this.chips.push(event.option.viewValue);
    this.chipInputControl.setValue(null);
  }

  toggleChipInput() {
    this.showChipInput = !this.showChipInput;
    if (this.showChipInput) {
      setTimeout(() => {
        this.chipInput.nativeElement.focus();
        this.openAutocomplete();
      });
    }
  }

  openAutocomplete() {
    this.chipInputControl.setValue('');

  }

  addChipFromAutocomplete(event: MatAutocompleteSelectedEvent) {
    const value = event.option.value;
    const currentChips = this.admin_settings.get('personalIdentifiableInformationInfo')?.value || [];
    if (value && !currentChips.some((chip: any) => chip.value === value.value)) {
      this.admin_settings.get('personalIdentifiableInformationInfo')?.setValue([...currentChips, value]);
      this.chipInputControl.setValue('');
    }
    this.showChipInput = false;
    this.updateFilteredChipOptions();
  }

  fetchChips() {
    // First try to get chips from institution settings (new API)
    if (this.institutionSettings?.personalIdentifiableInformationList) {
      const selectedChips = this.mapPersonalInfoChips(this.institutionSettings.personalIdentifiableInformationList);
      this.admin_settings.get('personalIdentifiableInformationInfo')?.setValue(selectedChips);
    } else {
      // Fallback to localStorage (legacy)
      const storedData = localStorage.getItem('user');
      if (storedData) {
        try {
          const institutionData = JSON.parse(storedData);
          if (institutionData?.Institution?.InstitutionSettings[0]) {
            const settings = institutionData.Institution.InstitutionSettings[0];
            const storedChips = settings.personalIdentifiableInformationList || [];

            // Map stored chip values to full chip objects
            const selectedChips = storedChips.map((value: string) => {
              const matchingOption = this.allChipOptions.find(option => option.value === value);
              return matchingOption || { value, title: value };
            });

            // Set the selected chips in the form
            this.admin_settings.get('personalIdentifiableInformationInfo')?.setValue(selectedChips);
          }
        } catch (error) {
          console.error('Error parsing localStorage data in fetchChips:', error);
        }
      }
    }
    this.updateFilteredChipOptions();
  }

  addChipFromDropdown(event: MatSelectChange) {
    const value = event.value;
    if (value && !this.chips.includes(value)) {
      this.chips.push(value);
      this.chipInputControl.setValue(null);
    }
    this.filterChips();
    this.showChipInput = false;
  }

  private updateFilteredChipOptions() {
    this.filteredChipOptions = this.chipInputControl.valueChanges.pipe(
      startWith(''),
      map((value: string | null) => this._filter(value || ''))
    );
  }

  private _filter(value: string): { value: string, title: string }[] {
    const filterValue = value.toLowerCase();
    const currentChips = this.admin_settings.get('personalIdentifiableInformationInfo')?.value || [];
    const currentChipValues = currentChips.map((chip: any) => chip.value);

    return this.allChipOptions
      .filter(option => !currentChipValues.includes(option.value))
      .filter(option =>
        option.value.toLowerCase().includes(filterValue) ||
        option.title.toLowerCase().includes(filterValue)
      );
  }

  private filterChips() {
    const currentChips = this.admin_settings.get('personalIdentifiableInformationInfo')?.value || [];
    const currentChipValues = currentChips.map((chip: any) => chip.value);
    console.log(currentChipValues, 'currentChipValues');

    const filteredOptions = this.allChipOptions.filter(option => !currentChipValues.includes(option.value));
    this.filteredChipOptions = of(filteredOptions);
    console.log(filteredOptions, 'filteredChipOptions');

    // Return allChipOptions that are not in currentChipValues
    return filteredOptions;
  }

  ngOnDestroy() {
    if (this.clickListener) {
      this.clickListener();
    }
  }

  private updateTrackColors() {
    const track = document.querySelector('.slider-track') as HTMLElement;
    if (!track) return;

    requestAnimationFrame(() => {
      // Ensure we have valid values
      const alteredStart = this.handles[0]?.value ?? 0;
      const exactStart = this.handles[1]?.value ?? 100;

      // Update the CSS variables
      track.style.setProperty('--altered-start', `${alteredStart}%`);
      track.style.setProperty('--exact-start', `${exactStart}%`);
    });
  }

  private updateHandleConstraints() {
    // Update Altered Text constraints
    this.handles[0].max = this.handles[1].value - 1;

    // Update Exact Match constraints
    this.handles[1].min = this.handles[0].value + 1;
  }

  // Add these methods to handle slider changes
  onSimilarityHandleChange(handles: Handle[]) {
    this.similarityHandles = [...handles];

    // Update form controls and allow event emission
    this.admin_settings.patchValue({
      alteredTextThreshold: handles[0].value,
      exactMatchThreshold: handles[1].value
    });

    // Update the CSS variables for the ranges
    const sliderElement = document.querySelector('.custom-slider') as HTMLElement;
    if (sliderElement) {
      sliderElement.style.setProperty('--altered-start', `${handles[0].value}%`);
      sliderElement.style.setProperty('--exact-start', `${handles[1].value}%`);
    }
  }

  onTranslationHandleChange(handles: Handle[]) {
    this.translationHandles = [...handles];

    // Update form control and allow event emission
    this.admin_settings.patchValue({
      contextualSimilaritiesThreshold: handles[1].value
    });

    // Update the CSS variable for the range
    const sliderElement = document.querySelector('.custom-slider') as HTMLElement;
    if (sliderElement) {
      sliderElement.style.setProperty('--contextual-start', `${handles[1].value}%`);
    }
  }

  // Update the validation method
  private validateThresholds(): boolean {
    if (this.admin_settings.get('enableThresholds')?.value) {
      const alteredThreshold = this.admin_settings.get('alteredTextThreshold')?.value;
      const exactThreshold = this.admin_settings.get('exactMatchThreshold')?.value;
      const contextualThreshold = this.admin_settings.get('contextualSimilaritiesThreshold')?.value;

      // Validate that values are within expected ranges
      if (alteredThreshold < 40 || alteredThreshold > 90 ||
        exactThreshold < 60 || exactThreshold > 90 ||
        (this.admin_settings.get('contextualSimilarity')?.value &&
          (contextualThreshold < 50 || contextualThreshold > 100))) {
        this.toastrService.error('Invalid threshold values');
        return false;
      }

      // Validate that altered threshold is less than exact threshold
      if (alteredThreshold >= exactThreshold) {
        this.toastrService.error('Altered text threshold must be less than exact match threshold');
        return false;
      }
    }
    return true;
  }

  // Add these methods for drag handling
  @HostListener('window:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (this.isDragging && this.activeHandle !== null) {
      const sliderRect = (event.target as HTMLElement)
        .closest('.slider-wrapper')
        ?.getBoundingClientRect();

      if (sliderRect) {
        const percentage = Math.min(Math.max(
          ((event.clientX - sliderRect.left) / sliderRect.width) * 100,
          0
        ), 100);

        const roundedPercentage = Math.round(percentage);
        // const handles = this.activeHandle === 0 ? this.similarityHandles : this.translationHandles;

        switch (this.activeHandle) {
          case 0: // Altered Text
            const maxAltered = Math.min(this.handles[1].value - 1, this.handles[0].max);
            this.handles[0].value = Math.min(Math.max(roundedPercentage, this.handles[0].min), maxAltered);
            break;

          case 1: // Exact Match
            const minExact = Math.max(this.handles[0].value + 1, this.handles[1].min);
            this.handles[1].value = Math.min(Math.max(roundedPercentage, minExact), this.handles[1].max);
            break;
        }

        this.updateHandleConstraints();
        this.updateTrackColors();
      }
    }
  }

  @HostListener('window:mouseup')
  onMouseUp() {
    this.isDragging = false;
    this.activeHandle = null;
  }

  startDragging(event: MouseEvent, index: number) {
    this.isDragging = true;
    this.activeHandle = index;
    event.preventDefault();
  }

  openLink(url: string): void {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  // Content Access Methods
  toggleContentAccess(): void {
    // The toggle state should be managed by the checkbox itself through ngModel
    this.admin_settings.patchValue({
      contentAccess: this.contentAccessEnabled ? 'open' : 'closed'
    });
  }

  processInstitutionsData(): void {
    this.institutionsList = [];
    console.log('Processing institutions data:', this.institutionsAccess);

    // Process client institutions first (clientInstitution)
    if (this.institutionsAccess.clientInstitution) {
      const clientInstitutions = this.institutionsAccess.clientInstitution.map((client) => ({
        id: client.id,
        name: client.name,
        hasChildren: client.Institution && client.Institution.length > 0,
        children: client.Institution ? client.Institution.map((institution) => ({
          id: institution.id,
          name: institution.name,
          // Get real access level from institutionContentAccessTarget
          accessLevel: this.determineAccessLevel(institution.institutionContentAccessTarget || [])
        })) : [],
        // Get real access level from client's institutionContentAccessTarget
        accessLevel: this.determineAccessLevel(client.institutionContentAccessTarget || []),
        isExpanded: false
      }));
      this.institutionsList.push(...clientInstitutions);
    }

    // Then process standalone institutions (institutionsWithoutClient)
    if (this.institutionsAccess.institutionsWithoutClient) {
      const standaloneInstitutions = this.institutionsAccess.institutionsWithoutClient.map((institution) => ({
        id: institution.id,
        name: institution.name,
        hasChildren: false,
        children: [],
        accessLevel: this.determineAccessLevel(institution.institutionContentAccessTarget),
        isExpanded: false
      }));
      this.institutionsList.push(...standaloneInstitutions);
    }

    console.log('Processed institutions list:', this.institutionsList);

    // Set total institutions count for pagination
    this.totalInstitutions = this.institutionsList.length;

    // Enable content access by default to show the table
    this.contentAccessEnabled = true;
  }

  determineAccessLevel(contentAccessTarget: any[]): string {
    // If institutionContentAccessTarget is empty array, it means closed
    if (!contentAccessTarget || contentAccessTarget.length === 0) {
      console.log('📋 Access Level: CLOSED (empty contentAccessTarget)');
      return 'closed';
    }

    console.log('📋 Content Access Target:', contentAccessTarget);

    // Get the accessType from the first item in the array
    // Assuming the structure has accessType property
    if (contentAccessTarget.length > 0 && contentAccessTarget[0].accessType) {
      const accessType = contentAccessTarget[0].accessType;
      console.log('📋 Access Level:', accessType.toUpperCase());
      return accessType;
    }

    // Fallback to closed if no accessType found
    console.log('📋 Access Level: CLOSED (no accessType found)');
    return 'closed';
  }

  // New method to determine parent access level based on children
  determineParentAccessLevel(institution: any): string | string[] {
    if (!institution.children || institution.children.length === 0) {
      return institution.accessLevel;
    }

    // Get unique access levels from children
    const childAccessLevels = [...new Set(institution.children.map((child: any) => child.accessLevel))];

    // If all children have the same access level, return that level
    if (childAccessLevels.length === 1) {
      return childAccessLevels[0] as string;
    }

    // If children have different access levels, return array of all levels
    return childAccessLevels as string[];
  }

  // Method to check if access level is mixed (array)
  isMixedAccessLevel(accessLevel: string | string[]): boolean {
    return Array.isArray(accessLevel);
  }

  toggleInstitutionExpansion(institutionId: string): void {
    const institution = this.institutionsList.find(inst => inst.id === institutionId);
    if (institution && institution.hasChildren) {
      institution.isExpanded = !institution.isExpanded;

      if (this.expandedInstitutions.has(institutionId)) {
        this.expandedInstitutions.delete(institutionId);
      } else {
        this.expandedInstitutions.add(institutionId);
      }
    }
  }

  toggleInstitutionSelection(institutionId: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    const institution = this.institutionsList.find(inst => inst.id === institutionId);
    if (!institution) return;

    if (this.selectedInstitutions.has(institutionId)) {
      // Deselecting parent - also deselect all children
      this.selectedInstitutions.delete(institutionId);
      const deselectedNames = [institution.name];

      if (institution.children) {
        institution.children.forEach((child: any) => {
          this.selectedInstitutions.delete(child.id);
          deselectedNames.push(`  └─ ${child.name}`);
        });
      }

      console.log(`🔴 DESELECTED Institution: ${institution.name}`, {
        institutionId,
        deselectedNames,
        totalSelected: this.selectedInstitutions.size
      });
    } else {
      // Selecting parent - also select all children
      this.selectedInstitutions.add(institutionId);
      const selectedNames = [institution.name];

      if (institution.children) {
        institution.children.forEach((child: any) => {
          this.selectedInstitutions.add(child.id);
          selectedNames.push(`  └─ ${child.name}`);
        });
      }

      console.log(`🔵 SELECTED Institution: ${institution.name}`, {
        institutionId,
        selectedNames,
        totalSelected: this.selectedInstitutions.size
      });
    }
  }

  toggleSubInstitutionSelection(parentId: string, childId: string, event: Event): void {
    event.stopPropagation();

    const parentInstitution = this.institutionsList.find(inst => inst.id === parentId);
    if (!parentInstitution) return;

    if (this.selectedInstitutions.has(childId)) {
      // Deselecting child
      this.selectedInstitutions.delete(childId);

      // If no children are selected, deselect parent
      const hasSelectedChildren = parentInstitution.children?.some((child: any) =>
        this.selectedInstitutions.has(child.id)
      );
      if (!hasSelectedChildren) {
        this.selectedInstitutions.delete(parentId);
      }
    } else {
      // Selecting child
      this.selectedInstitutions.add(childId);

      // If all children are selected, select parent
      const allChildrenSelected = parentInstitution.children?.every((child: any) =>
        this.selectedInstitutions.has(child.id)
      );
      if (allChildrenSelected) {
        this.selectedInstitutions.add(parentId);
      }
    }
  }

  changeAccessLevel(institutionId: string, accessLevel: string, isChild: boolean = false): void {
    if (isChild) {
      // Find the child institution
      for (const institution of this.institutionsList) {
        if (institution.children) {
          const child = institution.children.find((c: any) => c.id === institutionId);
          if (child) {
            child.accessLevel = accessLevel;
            break;
          }
        }
      }
    } else {
      const institution = this.institutionsList.find(inst => inst.id === institutionId);
      if (institution) {
        institution.accessLevel = accessLevel;
      }
    }

    // Track the pending change and trigger form change detection
    this.pendingAccessChanges[institutionId] = accessLevel;
    this.checkIfFormChanged();
  }

  selectAllInstitutions(): void {
    // Only select visible institutions
    const displayedInstitutions = this.getDisplayedInstitutions();
    const selectedIds: string[] = [];
    const selectedNames: string[] = [];

    displayedInstitutions.forEach(institution => {
      this.selectedInstitutions.add(institution.id);
      selectedIds.push(institution.id);
      selectedNames.push(institution.name);

      if (institution.children) {
        institution.children.forEach((child: any) => {
          this.selectedInstitutions.add(child.id);
          selectedIds.push(child.id);
          selectedNames.push(`  └─ ${child.name}`);
        });
      }
    });

    console.log('🔵 SELECT ALL - Selected institutions:', {
      count: selectedIds.length,
      displayedInstitutions: displayedInstitutions.length,
      totalInstitutions: this.institutionsList.length,
      selectedIds: selectedIds,
      selectedNames: selectedNames,
      allSelectedInstitutions: Array.from(this.selectedInstitutions)
    });
  }

  // Helper method to get total count of displayed institutions (parents + children)
  getTotalDisplayedInstitutionsCount(): number {
    let total = 0;
    const displayedInstitutions = this.getDisplayedInstitutions();
    displayedInstitutions.forEach(institution => {
      total++; // Count parent
      if (institution.children) {
        total += institution.children.length; // Count children
      }
    });
    return total;
  }

  // Helper method to check if all displayed institutions are selected
  areAllInstitutionsSelected(): boolean {
    const displayedInstitutions = this.getDisplayedInstitutions();
    if (displayedInstitutions.length === 0) return false;

    return displayedInstitutions.every(institution => {
      const isParentSelected = this.selectedInstitutions.has(institution.id);
      if (!institution.children || institution.children.length === 0) {
        return isParentSelected;
      }
      // For institutions with children, check if all children are selected
      return institution.children.every((child: any) =>
        this.selectedInstitutions.has(child.id)
      );
    });
  }

  // Helper method to check if some displayed institutions are selected (for indeterminate state)
  areSomeInstitutionsSelected(): boolean {
    const displayedInstitutions = this.getDisplayedInstitutions();
    if (displayedInstitutions.length === 0) return false;

    const hasAnySelected = displayedInstitutions.some(institution => {
      const isParentSelected = this.selectedInstitutions.has(institution.id);
      if (!institution.children || institution.children.length === 0) {
        return isParentSelected;
      }
      // For institutions with children, check if any child is selected
      return institution.children.some((child: any) =>
        this.selectedInstitutions.has(child.id)
      );
    });

    return hasAnySelected && !this.areAllInstitutionsSelected();
  }

  deselectAllInstitutions(): void {
    // Only deselect visible institutions (not all)
    const displayedInstitutions = this.getDisplayedInstitutions();
    const deselectedIds: string[] = [];
    const deselectedNames: string[] = [];

    displayedInstitutions.forEach(institution => {
      if (this.selectedInstitutions.has(institution.id)) {
        this.selectedInstitutions.delete(institution.id);
        deselectedIds.push(institution.id);
        deselectedNames.push(institution.name);
      }

      if (institution.children) {
        institution.children.forEach((child: any) => {
          if (this.selectedInstitutions.has(child.id)) {
            this.selectedInstitutions.delete(child.id);
            deselectedIds.push(child.id);
            deselectedNames.push(`  └─ ${child.name}`);
          }
        });
      }
    });

    console.log('🔴 DESELECT ALL - Deselected institutions:', {
      count: deselectedIds.length,
      displayedInstitutions: displayedInstitutions.length,
      totalInstitutions: this.institutionsList.length,
      deselectedIds: deselectedIds,
      deselectedNames: deselectedNames,
      remainingSelectedInstitutions: Array.from(this.selectedInstitutions)
    });
  }

  applyBulkAccessLevel(): void {
    // Save the access changes locally instead of sending to backend immediately
    this.selectedInstitutions.forEach(institutionId => {
      // Check if this is a parent institution or child institution
      const isParentInstitution = this.institutionsList.some(inst => inst.id === institutionId);

      if (isParentInstitution) {
        // Handle parent institution
        this.changeAccessLevel(institutionId, this.bulkAccessLevel, false);
      } else {
        // Handle child institution - find which parent it belongs to
        let isChildInstitution = false;
        for (const institution of this.institutionsList) {
          if (institution.children) {
            const child = institution.children.find((c: any) => c.id === institutionId);
            if (child) {
              this.changeAccessLevel(institutionId, this.bulkAccessLevel, true);
              isChildInstitution = true;
              break;
            }
          }
        }
      }

      // Track the pending change
      this.pendingAccessChanges[institutionId] = this.bulkAccessLevel;
    });

    // Trigger form change detection to enable submit button
    this.checkIfFormChanged();

    this.toastrService.success('Access levels updated locally. Click Submit to save all settings.');
    this.selectedInstitutions.clear();

    console.log('📝 PENDING ACCESS CHANGES:', this.pendingAccessChanges);
  }

  getAccessLevelLabel(accessLevel: string): string {
    switch (accessLevel) {
      case 'open': return this.translate.instant('new_entries.open_access');
      case 'closed': return this.translate.instant('new_entries.closed_access');
      case 'restricted': return this.translate.instant('new_entries.restricted_access');
      default: return this.translate.instant('new_entries.closed_access');
    }
  }

  // Helper method to check if parent institution is in indeterminate state
  isParentIndeterminate(institution: any): boolean {
    if (!institution.children || institution.children.length === 0) {
      return false;
    }

    const selectedChildren = institution.children.filter((child: any) =>
      this.selectedInstitutions.has(child.id)
    );

    return selectedChildren.length > 0 && selectedChildren.length < institution.children.length;
  }

  // Helper method to check if parent institution should be checked
  isParentChecked(institution: any): boolean {
    if (!institution.children || institution.children.length === 0) {
      return this.selectedInstitutions.has(institution.id);
    }

    return institution.children.every((child: any) =>
      this.selectedInstitutions.has(child.id)
    );
  }

  // Pagination methods for institutions list
  getDisplayedInstitutions(): any[] {
    const pageSizeNum = Number(this.pageSize);
    const startIndex = (this.page - 1) * pageSizeNum;
    const endIndex = Math.min(startIndex + pageSizeNum, this.totalInstitutions);
    return this.institutionsList.slice(startIndex, endIndex);
  }

  /**
   * Handle page size change
   */
  onInstitutionsPageSizeChange(): void {
    // Convert pageSize to number to ensure proper calculations
    this.pageSize = Number(this.pageSize);
    this.page = 1;
    // Trigger change detection to update the view
    this.cdr.detectChanges();
  }

  /**
   * Change page in pagination
   * @param newPage Page number
   */
  changeInstitutionsPage(newPage: number): void {
    this.page = newPage;
    // Trigger change detection to update the view
    this.cdr.detectChanges();
  }

  getRemainingCount(): number {
    const pageSizeNum = Number(this.pageSize);
    return this.institutionsList.length - (this.page * pageSizeNum);
  }

  // Method to check if Save Changes button should be enabled
  canSaveAccessChanges(): boolean {
    return this.selectedInstitutions.size > 0;
  }

  // Method to prepare access content changes for backend API
  prepareAccessContentForBackend(): { id: string, access: string }[] {
    const accessContent: { id: string, access: string }[] = [];

    // Add all institutions with their current access levels
    this.institutionsList.forEach(institution => {
      // Add parent institution
      accessContent.push({
        id: institution.id,
        access: institution.accessLevel
      });

      // Add child institutions if any
      if (institution.children && institution.children.length > 0) {
        institution.children.forEach((child: any) => {
          accessContent.push({
            id: child.id,
            access: child.accessLevel
          });
        });
      }
    });

    console.log('📤 PREPARED ACCESS CONTENT FOR BACKEND:', accessContent);
    return accessContent;
  }

  // Method to get access level counts for parent institutions with mixed access
  getAccessLevelCounts(institution: any): { [key: string]: number } {
    if (!institution.children || institution.children.length === 0) {
      return {};
    }

    const accessCounts: { [key: string]: number } = {};

    institution.children.forEach((child: any) => {
      const accessLevel = child.accessLevel;
      accessCounts[accessLevel] = (accessCounts[accessLevel] || 0) + 1;
    });

    return accessCounts;
  }

  // Method to get formatted access label with count
  getAccessLabelWithCount(accessLevel: string, count: number, institution: any): string {
    const baseLabel = this.getAccessLevelLabel(accessLevel);

    // Only show count if there are mixed access levels in the institution
    if (this.isMixedAccessLevel(this.determineParentAccessLevel(institution))) {
      return `${baseLabel} (${count})`;
    }

    return baseLabel;
  }

}

